import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Draggable from 'react-draggable';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  X,
  Minimize2,
  Camera,
  RotateCcw,
  SwitchCamera,
  Loader2,
  CheckCircle2,
  Eye,
  Barcode,
  Upload,
  ImageIcon
} from 'lucide-react';
import CameraFeed from './CameraFeed';
import { useCameraStream } from '../../hooks/useCameraStream';
import { useXimilarScanner } from '../../hooks/useXimilarScanner';

/**
 * Floating draggable overlay for vision-based item detection
 * Uses Ximilar Visual Search API for AI-powered similarity matching
 */
const VisionDetectorOverlay = ({
  shipmentItems = [],
  onDetection,
  onClearFilter,
  onItemSelect,
  onClose,
  hasActiveFilter = false,
  className = ''
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [lastMatch, setLastMatch] = useState(null);
  const [showScoredList, setShowScoredList] = useState(true);
  const [testMode, setTestMode] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  // Camera stream
  const {
    videoRef,
    error: cameraError,
    isLoading: cameraLoading,
    switchCamera,
    reinitialize
  } = useCameraStream({
    enabled: !isMinimized,
    facingMode: 'environment'
  });

  // Ximilar visual search scanner - manual mode (no auto-scanning)
  const {
    isIndexing,
    indexedCount,
    indexError,
    isScanning,
    scoredItems,
    barcodeMatch,
    lastSearchResult,
    reset: resetScanner,
    scanImage,
    captureAndSearch,
    reindex
  } = useXimilarScanner({
    videoRef,
    shipmentItems,
    enabled: !isMinimized && !cameraError,
    autoScan: false, // Manual capture mode
    onMatch: (matchedItem) => {
      setLastMatch(matchedItem);
      // Report detection to parent
      const identifier = matchedItem.item?.asin ||
                         matchedItem.item?.fnsku ||
                         matchedItem.item?.merchant_sku;
      if (identifier) {
        onDetection?.([identifier]);
      }
    }
  });

  // Determine current status for UI
  const status = useMemo(() => {
    if (barcodeMatch) return 'matched';
    if (scoredItems.length > 0 && scoredItems[0]?.similarity > 0.75) return 'high-confidence';
    if (scoredItems.length > 0 && scoredItems[0]?.similarity > 0.5) return 'detecting';
    if (isScanning) return 'scanning';
    return 'idle';
  }, [barcodeMatch, scoredItems, isScanning]);

  // Top confidence score
  const topConfidence = useMemo(() => {
    if (barcodeMatch) return 1.0;
    if (scoredItems.length > 0) return scoredItems[0].similarity;
    return 0;
  }, [barcodeMatch, scoredItems]);

  // Clear match after 3 seconds
  useEffect(() => {
    if (lastMatch) {
      const timer = setTimeout(() => setLastMatch(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastMatch]);

  // Handle item click in scored list
  const handleItemClick = useCallback((item) => {
    const identifier = item.item?.asin || item.item?.fnsku || item.item?.merchant_sku;
    if (identifier) {
      onItemSelect?.(item);
      onDetection?.([identifier]);
    }
  }, [onDetection, onItemSelect]);

  // Handle close
  const handleClose = useCallback(() => {
    resetScanner();
    onClose?.();
  }, [resetScanner, onClose]);

  // Handle manual capture button press
  const handleCapture = useCallback(async () => {
    if (isScanning || isIndexing) return;

    setDebugInfo({ status: 'scanning' });

    const startTime = performance.now();
    const result = await captureAndSearch();
    const elapsed = Math.round(performance.now() - startTime);

    // Store debug info
    setDebugInfo({
      status: 'complete',
      elapsed: `${elapsed}ms`,
      apiTime: result?.processingTime ? `${result.processingTime}ms` : null,
      resultType: result?.type || 'unknown',
      barcode: result?.barcode || null,
      autoCrop: result?.rawResponse?.autoCrop || null,
      topMatch: result?.match ? {
        asin: result.match.item?.asin,
        sku: result.match.item?.merchant_sku,
        title: result.match.item?.title?.substring(0, 50),
        confidence: result.match.confidence ? `${Math.round(result.match.confidence * 100)}%` : null
      } : null,
      allScores: result?.scores?.slice(0, 10).map(s => ({
        asin: s.item?.asin,
        sku: s.item?.merchant_sku,
        similarity: `${Math.round((s.similarity || 0) * 100)}%`
      })) || [],
      rawResponse: result?.rawResponse,
      indexedCount,
      error: result?.error
    });

    console.log('[Capture] Result:', result);
  }, [captureAndSearch, isScanning, isIndexing, indexedCount]);

  // Handle file upload for test mode
  const handleFileUpload = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) return;

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setUploadedImage(previewUrl);
    setDebugInfo({ status: 'scanning', file: file.name, size: file.size });

    // Scan the image
    const startTime = performance.now();
    const result = await scanImage(file);
    const elapsed = Math.round(performance.now() - startTime);

    // Store debug info
    setDebugInfo({
      status: 'complete',
      file: file.name,
      size: file.size,
      elapsed: `${elapsed}ms`,
      apiTime: result?.processingTime ? `${result.processingTime}ms` : null,
      resultType: result?.type || 'unknown',
      barcode: result?.barcode || null,
      autoCrop: result?.rawResponse?.autoCrop || null,
      topMatch: result?.match ? {
        asin: result.match.item?.asin,
        sku: result.match.item?.merchant_sku,
        title: result.match.item?.title?.substring(0, 50),
        confidence: result.match.confidence ? `${Math.round(result.match.confidence * 100)}%` : null,
        ximilarData: result.match.ximilarData
      } : null,
      allScores: result?.scores?.slice(0, 10).map(s => ({
        asin: s.item?.asin,
        sku: s.item?.merchant_sku,
        similarity: `${Math.round((s.similarity || 0) * 100)}%`,
        ximilarId: s.ximilarData?._id
      })) || [],
      rawResponse: result?.rawResponse,
      indexedCount
    });

    console.log('[Test] Scan result:', result);
  }, [scanImage, indexedCount]);

  // Handle file input change
  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  // Handle drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDraggingFile(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDraggingFile(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  // Handle paste from clipboard
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          handleFileUpload(file);
          break;
        }
      }
    }
  }, [handleFileUpload]);

  // Add paste listener when in test mode
  useEffect(() => {
    if (testMode) {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [testMode, handlePaste]);

  // Toggle test mode
  const toggleTestMode = useCallback(() => {
    setTestMode(prev => !prev);
    setUploadedImage(null);
    resetScanner();
  }, [resetScanner]);

  // Get confidence ring color
  const getConfidenceColor = (confidence, isBarcode = false) => {
    if (isBarcode) return 'ring-green-500 bg-green-500/10';
    if (confidence > 0.8) return 'ring-blue-500 bg-blue-500/10';
    if (confidence > 0.6) return 'ring-yellow-500 bg-yellow-500/10';
    return 'ring-gray-300 bg-gray-100';
  };

  // Minimized view
  if (isMinimized) {
    return (
      <Draggable bounds="parent">
        <div
          className={`
            fixed bottom-4 right-4 z-50
            bg-blue-600 text-white
            p-3 rounded-full shadow-lg
            cursor-move hover:bg-blue-700
            transition-colors
            ${className}
          `}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMinimized(false)}
            className="text-white hover:bg-blue-700 p-0"
          >
            <Camera className="w-6 h-6" />
          </Button>
          {(barcodeMatch || (scoredItems[0]?.similarity > 0.75)) && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-3 h-3" />
            </div>
          )}
        </div>
      </Draggable>
    );
  }

  return (
    <Draggable handle=".drag-handle" bounds="parent">
      <Card
        className={`
          fixed bottom-4 right-4 z-50
          w-96 shadow-2xl overflow-hidden
          bg-white border-slate-200
          ${className}
        `}
      >
        {/* Header */}
        <div className="drag-handle bg-gradient-to-r from-blue-600 to-blue-700 text-white p-3 flex items-center justify-between cursor-move">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            <span className="font-semibold text-sm">Vision Scanner</span>
            {testMode && (
              <span className="text-xs bg-yellow-500 px-2 py-0.5 rounded-full">
                TEST
              </span>
            )}
            {isIndexing && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            {indexedCount > 0 && !testMode && (
              <span className="text-xs bg-blue-500 px-2 py-0.5 rounded-full">
                {indexedCount} indexed
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Test mode toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTestMode}
              className={`p-1 h-7 w-7 ${testMode ? 'bg-yellow-500 hover:bg-yellow-600' : 'text-white hover:bg-blue-800'}`}
              title={testMode ? "Exit test mode" : "Test with image upload"}
            >
              <Upload className="w-4 h-4" />
            </Button>
            {!testMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={switchCamera}
                className="text-white hover:bg-blue-800 p-1 h-7 w-7"
                title="Switch camera"
              >
                <SwitchCamera className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(true)}
              className="text-white hover:bg-blue-800 p-1 h-7 w-7"
              title="Minimize"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="text-white hover:bg-blue-800 p-1 h-7 w-7"
              title="Close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Camera feed or Test Mode Upload */}
        <div className="relative">
          {testMode ? (
            /* Test Mode: Image Upload Zone */
            <div
              className={`
                h-52 flex flex-col items-center justify-center p-4 transition-colors
                ${isDraggingFile ? 'bg-blue-100 border-blue-400' : 'bg-slate-100'}
                ${uploadedImage ? 'p-0' : ''}
              `}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {uploadedImage ? (
                /* Show uploaded image */
                <div className="relative w-full h-full">
                  <img
                    src={uploadedImage}
                    alt="Test image"
                    className="w-full h-full object-contain bg-black"
                  />
                  {isScanning && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}
                  {/* Re-upload button */}
                  <label className="absolute bottom-2 right-2 cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <div className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 hover:bg-blue-700">
                      <Upload className="w-3 h-3" />
                      New Image
                    </div>
                  </label>
                </div>
              ) : (
                /* Upload prompt */
                <>
                  <ImageIcon className={`w-12 h-12 mb-3 ${isDraggingFile ? 'text-blue-500' : 'text-slate-400'}`} />
                  <p className="text-sm text-slate-600 text-center mb-3">
                    {isDraggingFile ? 'Drop image here' : 'Drag, paste, or upload an image'}
                  </p>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <div className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-blue-700">
                      <Upload className="w-4 h-4" />
                      Upload Image
                    </div>
                  </label>
                  <p className="text-xs text-slate-400 mt-2">
                    Ctrl+V to paste from clipboard
                  </p>
                </>
              )}
            </div>
          ) : cameraError ? (
            <div className="h-52 bg-slate-100 flex flex-col items-center justify-center text-slate-600 p-4">
              <Camera className="w-12 h-12 mb-3 text-slate-400" />
              <p className="text-sm text-center mb-3">{cameraError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={reinitialize}
                className="gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Retry
              </Button>
            </div>
          ) : cameraLoading || isIndexing ? (
            <div className="h-52 bg-slate-100 flex flex-col items-center justify-center gap-3">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
              <span className="text-sm text-slate-600">
                {isIndexing ? 'Indexing products in Ximilar...' : 'Starting camera...'}
              </span>
            </div>
          ) : (
            <div className="relative">
              <CameraFeed
                videoRef={videoRef}
                status={status}
                confidence={topConfidence}
                lastMatch={lastMatch}
                className="h-52"
              />
              {/* Manual Capture Button - overlaid on camera */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                <Button
                  onClick={handleCapture}
                  disabled={isScanning || indexedCount === 0}
                  className={`
                    w-16 h-16 rounded-full shadow-lg
                    ${isScanning
                      ? 'bg-yellow-500 hover:bg-yellow-600'
                      : 'bg-blue-600 hover:bg-blue-700'
                    }
                    flex items-center justify-center
                    transition-all duration-200
                    ${isScanning ? 'animate-pulse' : ''}
                  `}
                >
                  {isScanning ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : (
                    <Camera className="w-8 h-8 text-white" />
                  )}
                </Button>
              </div>
              {/* Indexed count indicator */}
              {indexedCount > 0 && (
                <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {indexedCount} items indexed
                </div>
              )}
            </div>
          )}
        </div>

        {/* Debug panel - show when there's debug info */}
        {debugInfo && (
          <div className="border-t border-slate-200 bg-slate-900 text-slate-100 p-3 text-xs font-mono max-h-48 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 font-semibold">DEBUG OUTPUT</span>
              <button
                onClick={() => setDebugInfo(null)}
                className="text-slate-500 hover:text-slate-300"
              >
                ×
              </button>
            </div>

            {debugInfo.status === 'scanning' ? (
              <div className="text-yellow-400">Scanning image with Ximilar...</div>
            ) : (
              <div className="space-y-1">
                <div><span className="text-slate-500">Status:</span> <span className={
                  debugInfo.resultType === 'barcode' ? 'text-green-400' :
                  debugInfo.resultType === 'ximilar' ? 'text-blue-400' :
                  debugInfo.resultType === 'error' ? 'text-red-400' :
                  'text-yellow-400'
                }>{debugInfo.resultType}</span></div>
                <div><span className="text-slate-500">Total Time:</span> {debugInfo.elapsed}</div>
                {debugInfo.apiTime && (
                  <div><span className="text-slate-500">Ximilar API:</span> {debugInfo.apiTime}</div>
                )}
                <div><span className="text-slate-500">Indexed:</span> {debugInfo.indexedCount} items in collection</div>

                {debugInfo.autoCrop && (
                  <div className="mt-1 p-1 bg-slate-800 rounded">
                    <span className="text-slate-500">Auto-Crop:</span>{' '}
                    <span className={debugInfo.autoCrop.cropped ? 'text-green-400' : 'text-yellow-400'}>
                      {debugInfo.autoCrop.detection || 'none'}
                    </span>
                    {debugInfo.autoCrop.confidence && (
                      <span className="text-slate-400"> ({Math.round(debugInfo.autoCrop.confidence * 100)}%)</span>
                    )}
                    {debugInfo.autoCrop.cropTime && (
                      <span className="text-slate-500 ml-2">{debugInfo.autoCrop.cropTime}ms</span>
                    )}
                  </div>
                )}

                {debugInfo.barcode && (
                  <div><span className="text-slate-500">Barcode:</span> <span className="text-green-400">{debugInfo.barcode}</span></div>
                )}

                {debugInfo.topMatch && (
                  <div className="mt-2 p-2 bg-slate-800 rounded">
                    <div className="text-blue-400 font-semibold mb-1">Top Match:</div>
                    <div><span className="text-slate-500">ASIN:</span> {debugInfo.topMatch.asin || 'N/A'}</div>
                    <div><span className="text-slate-500">SKU:</span> {debugInfo.topMatch.sku || 'N/A'}</div>
                    <div><span className="text-slate-500">Confidence:</span> <span className="text-green-400">{debugInfo.topMatch.confidence}</span></div>
                    <div className="text-slate-400 truncate">{debugInfo.topMatch.title}</div>
                  </div>
                )}

                {debugInfo.allScores?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-slate-400 font-semibold mb-1">All Scores:</div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500">
                          <th className="text-left">ASIN/SKU</th>
                          <th className="text-right">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {debugInfo.allScores.map((score, i) => (
                          <tr key={i} className={i === 0 ? 'text-green-400' : ''}>
                            <td className="truncate max-w-[200px]">{score.asin || score.sku}</td>
                            <td className="text-right">{score.similarity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {debugInfo.resultType === 'none' && (
                  <div className="text-yellow-400 mt-2">No match found above threshold</div>
                )}

                {debugInfo.resultType === 'error' && (
                  <div className="text-red-400 mt-2">Error: {debugInfo.error}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Scored items list - dynamic reordering (only show items with >75% confidence) */}
        {indexedCount > 0 && scoredItems.filter(item => item.similarity > 0.75).length > 0 && (
          <div className="border-t border-slate-200">
            <div
              className="px-3 py-2 bg-slate-50 flex items-center justify-between cursor-pointer"
              onClick={() => setShowScoredList(!showScoredList)}
            >
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Eye className="w-4 h-4" />
                <span>Best Matches</span>
              </div>
              <span className="text-xs text-slate-500">
                {showScoredList ? 'Hide' : 'Show'}
              </span>
            </div>

            {showScoredList && (
              <div className="max-h-40 overflow-y-auto">
                {scoredItems.filter(item => item.similarity > 0.75).slice(0, 5).map((item, index) => {
                  const isBarcode = item.matchType === 'barcode';
                  const confidence = isBarcode ? 1 : item.similarity;
                  const confidencePercent = Math.round(confidence * 100);

                  return (
                    <div
                      key={item.itemId || index}
                      onClick={() => handleItemClick(item)}
                      className={`
                        px-3 py-2 flex items-center gap-3 cursor-pointer
                        hover:bg-slate-50 transition-all duration-300
                        border-b border-slate-100 last:border-b-0
                        ${index === 0 && confidence > 0.7 ? 'bg-blue-50' : ''}
                      `}
                      style={{
                        transform: `translateY(0)`,
                        transition: 'transform 0.3s ease-out'
                      }}
                    >
                      {/* Confidence ring around image */}
                      <div className={`
                        relative w-10 h-10 rounded-lg overflow-hidden
                        ring-2 ${getConfidenceColor(confidence, isBarcode)}
                      `}>
                        {item.item?.images?.[0]?.large_url || item.item?.image_url ? (
                          <img
                            src={item.item?.images?.[0]?.large_url || item.item?.image_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                            <Camera className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                      </div>

                      {/* Item info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {item.item?.title || item.item?.asin || 'Unknown'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {item.item?.merchant_sku || item.item?.asin}
                        </p>
                      </div>

                      {/* Confidence badge */}
                      <div className="flex items-center gap-1">
                        {isBarcode ? (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            <Barcode className="w-3 h-3" />
                            <span>100%</span>
                          </div>
                        ) : (
                          <div className={`
                            px-2 py-1 rounded-full text-xs font-medium
                            ${confidence > 0.8 ? 'bg-blue-100 text-blue-700' :
                              confidence > 0.6 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'}
                          `}>
                            {confidencePercent}%
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Index error display */}
        {indexError && (
          <div className="px-3 py-2 bg-red-50 text-red-600 text-xs border-t border-red-200">
            Ximilar Error: {indexError}
          </div>
        )}

        {/* Active filter indicator with clear button */}
        {hasActiveFilter && (
          <div className="px-3 py-2 bg-blue-50 border-t border-blue-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
              <Eye className="w-4 h-4" />
              <span>Filter Active</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onClearFilter?.();
                resetScanner();
              }}
              className="h-7 px-2 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
            >
              <X className="w-4 h-4 mr-1" />
              Clear Filter
            </Button>
          </div>
        )}

        {/* Status bar */}
        <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
          <div className="flex items-center gap-2">
            {status === 'matched' && (
              <>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-green-600 font-medium">Barcode Match!</span>
              </>
            )}
            {status === 'high-confidence' && (
              <>
                <Eye className="w-4 h-4 text-blue-500" />
                <span className="text-blue-600 font-medium">High confidence match</span>
              </>
            )}
            {status === 'detecting' && (
              <>
                <Eye className="w-4 h-4 text-yellow-500" />
                <span>Analyzing...</span>
              </>
            )}
            {status === 'scanning' && (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Scanning...</span>
              </>
            )}
            {status === 'idle' && (
              <span>{testMode ? 'Upload an image to test' : 'Hold item in center'}</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Barcode className="w-3 h-3" />
            <span>+</span>
            <Eye className="w-3 h-3" />
          </div>
        </div>
      </Card>
    </Draggable>
  );
};

export default VisionDetectorOverlay;
